/**
 * API Configuration
 * Centralized API endpoint configuration for the frontend.
 * 
 * To migrate to V2 endpoints, change API_VERSION from '' to '/v2'
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// API Version - Change to '/v2' to use new modular endpoints
// Keep as '' to use legacy V1 endpoints
const API_VERSION = '/v2';

// Base API URL
export const API_BASE = `${BACKEND_URL}/api${API_VERSION}`;

// Legacy V1 API (for backwards compatibility)
export const API_V1 = `${BACKEND_URL}/api`;

// V2 API (new modular endpoints)
export const API_V2 = `${BACKEND_URL}/api/v2`;

// Default export for easy migration
export const API = API_BASE;

// Helper function to get full URL
export const getApiUrl = (path) => `${API_BASE}${path}`;

// Helper for V1 endpoints (use during migration)
export const getV1Url = (path) => `${API_V1}${path}`;

// Helper for V2 endpoints
export const getV2Url = (path) => `${API_V2}${path}`;

// Session token storage key
const SESSION_TOKEN_KEY = 'pe_session_token';

// Storage helper that works in iOS Safari (handles private browsing)
const getStorage = () => {
  try {
    // Test if localStorage is available
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return localStorage;
  } catch (e) {
    // Fallback to sessionStorage for iOS private browsing
    return sessionStorage;
  }
};

// Save session token
export const saveSessionToken = (token) => {
  if (token) {
    try {
      getStorage().setItem(SESSION_TOKEN_KEY, token);
    } catch (e) {
      console.warn('Could not save session token:', e);
    }
  }
};

// Get session token
export const getSessionToken = () => {
  try {
    return getStorage().getItem(SESSION_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

// Clear session token
export const clearSessionToken = () => {
  try {
    getStorage().removeItem(SESSION_TOKEN_KEY);
  } catch (e) {
    console.warn('Could not clear session token:', e);
  }
};

// Create axios instance with auth interceptor
export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Request interceptor to add auth header
apiClient.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  return config;
});

// GLOBAL interceptor - Add X-Session-Token to ALL axios requests
// This ensures authenticated endpoints work in production even with cross-domain cookies issues
axios.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  // Ensure withCredentials is set for all requests
  config.withCredentials = true;
  return config;
});

// Global response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401, clear the session and redirect to login
    // But only for protected pages — not on public routes
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === '/login';
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      const publicPaths = ['/', '/pricing', '/features', '/terms', '/privacy', '/reset-password', '/market', '/market-analysis'];
      const isPublicPage = publicPaths.includes(currentPath);
      
      // Don't redirect for auth endpoints (they handle their own flow)
      // Don't redirect if already on login page or on a public page
      if (!isLoginPage && !isAuthEndpoint && !isPublicPage) {
        console.warn('Session expired, redirecting to login');
        clearSessionToken();
        // Use a small delay to ensure state cleanup
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

export default API;
